<template>
	<div class="component-container component-notification-tray">
		<div v-click-outside="closeDropdown" class="notifications menu-item" @click="toggleActive">
			<fa class="header-icon" :icon="['far', 'bell']" />
			<p v-if="hasActiveNotifications" class="header-count"></p>
		</div>

		<div v-if="active" class="notifications-container">
			<div class="header">
				<p class="title">
					Notifications
				</p>
				<p class="icon" title="Clear Notifications" @click="clearNotifications">
					<fa :icon="['far', 'trash-can']" />
				</p>
			</div>
			<div class="notifications-list type-active">
				<div v-for="(notification, index) in activeNotifications" :key="index" class="notification-item active" :class="[`bc-alert-${notification.type}`]" @click="goToContext(notification)">
					<p class="title" v-text="notification.title"></p>
					<p class="message" v-text="notification.message"></p>
					<fa class="pin-icon" :icon="['fas', 'thumbtack']" title="This is an active notification and is pinned by default." />
					<div v-if="notification.progress" class="progress-bar-container">
						<div class="progress-bar">
							<div class="bar" :style="{width: `${notification.progress}%`}">
								<!-- Empty -->
							</div>
							<div class="bar-pulse" :style="{'max-width': `${notification.progress}%`}">
								<!-- Empty -->
							</div>
						</div>
					</div>
				</div>

				<div v-for="(notification, index) in passiveNotifications" :key="index" class="notification-item passive" :class="[`bc-alert-${notification.type}`]">
					<p class="message" v-text="notification.message"></p>
					<p v-if="notification.repeated > 0" class="repeated" v-text="`x${notification.repeated}`"></p>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { Options, Vue } from 'vue-class-component';

	@Options({
		name: 'Notification Tray',
	})
	export default class NotificationTray extends Vue {

		public active = false;

		public get hasActiveNotifications(): boolean {
			return this.$store.state.notification.activeNotifications.length > 0;
		}

		public get activeNotifications(): Array<any> {
			return this.$store.state.notification.activeNotifications;
		}

		public get passiveNotifications(): Array<any> {
			return this.$store.state.notification.pastNotifications;
		}

		public goToContext(notification: any): void {
			switch (notification.type) {
			case 'application':
				this.$router.push(`/create/applications/${notification.applicationMuid}/overview`);
				break;
			case 'applicationEnvironment':
				this.$router.push(`/create/application/${notification.applicationMuid}/${notification.environmentId}/overview`);
				break;
			case 'applicationEnvironmentUpgrade':
				this.$router.push(`/create/application/${notification.applicationMuid}/${notification.environmentId}/upgrade`);
				break;
			case 'backup':
				this.$router.push(`/create/application/${notification.applicationMuid}/${notification.environmentId}/backups`);
				break;
			case 'snapshot':
				this.$router.push(`/create/application/${notification.applicationMuid}/${notification.environmentId}/snapshots`);
				break;
			default:
				this.$router.push(`/create/application/${notification.applicationMuid}/${notification.environmentId}/overview`);
				break;
			}
		}

		public clearNotifications(): void {
			this.$store.dispatch('notification/clear');
		}

		public toggleActive(): void {
			this.active = !this.active;
		}

		public closeDropdown(): void {
			this.active = false;
		}
	}
</script>

<style lang="scss" scoped>
	@import '../assets/styles/base';

	.component-container {
		text-align: center;
	}

	.menu-item {
		text-align: center;
		height: 32px;
		width: 32px;
		display: inline-block;
		border-radius: 50%;
		padding-top: 9px;
		margin-top: 2px;
		position: relative;
		cursor: pointer;
		color: white;
		background-color: $sidebar-create-color;
		@include prefix(transition, background-color 0.5s ease-in-out);
		&:hover {
			background-color: darken($sidebar-create-color, 10%);
			@include prefix(transition, background-color 0.3s ease-in-out);
		}

		.header-icon {
			font-size: 16px;
			position: relative;
			top: -1px;
		}

		.header-count {
			font-size: 12px;
			position: absolute;
			height: 10px;
			width: 10px;
			top: 4px;
			right: -2px;
			background-color: #25c281;
			border-radius: 50%;
		}

	}

	.notifications-container {
		position: absolute;
		top: calc(#{$header-height} + 10px);
		width: 300px;
		right: 20px;
		background-color: white;
		border-radius: 10px 6px 10px 10px;
		@include clearfix();
		@include prefix(box-shadow, 0 3px 5px 0 rgba($sidebar-create-color, 0.3));

		.header {
			padding: 10px;
			@include clearfix();

			.title {
				float: left;
				width: auto;
				color: $sidebar-controller-color;
				font-weight: bold;
			}

			.icon {
				float: right;
				width: auto;
				cursor: pointer;
				color: $sidebar-create-color;
				&:hover {
					color: darken($sidebar-create-color, 10%);
				}
			}
		}

		.notifications-list {
			max-height: 450px;
			min-height: fit-content;
			overflow-y: auto;
		}

		&:after {
			content: '';
			width: 0;
			height: 0;
			border-left: 8px solid transparent;
			border-right: 8px solid transparent;
			border-bottom: 8px solid white;
			position: absolute;
			top: -8px;
			right: 63px;
		}

		.notification-item {
			float: left;
			text-align: left;
			margin: 0.5rem 0.5rem 0px 0.5rem;
			padding: 10px;
			background-color: rgba($sidebar-create-color, 0.05);
			width: calc(100% - 1rem);
			border-radius: 8px;
			border-left: 8px solid #e3e3e3;
			@include prefix(transition, all 0.2s ease-in-out);
			&:last-of-type {
				margin-bottom: 0.5rem !important;
			}
			&:hover {
				background-color: rgba($sidebar-create-color, 0.1);
				.progress-bar {
					background-color: darken(#e3e3e3, 2%) !important;
				}
			}

			&:hover.type-active {
				background-color: #e3e3e3;
			}

			&.passive {
				padding-right: 30px;
				position: relative;

				.message {
					font-size: 12px;
					font-weight: 400;
					color: $sidebar-controller-color;
				}

				.repeated {
					position: absolute;
					top: 10px;
					right: 10px;
					color: rgba($sidebar-create-color, 0.6);
					font-size: 12px;
				}
			}

			&.active {
				border: none;
				/* border-bottom: 1px solid #e3e3e3; */
				position: relative;

				.title {
					float: left;
					width: 90%;
					text-align: left;
					font-weight: 600;
					font-size: 12px;
					padding-bottom: 5px;
					padding-left: 10px;
					color: $sidebar-controller-color;
				}

				.message {
					float: left;
					width: 100%;
					font-weight: normal;
					font-size: 12px;
					padding-left: 10px;
					color: rgba($sidebar-controller-color, 0.7);
				}

				.pin-icon {
					position: absolute;
					right: 10px;
					top: 10px;
					color: rgba($sidebar-create-color, 0.7);
					font-size: 12px;
					// cursor: not-allowed;
				}

				.progress-bar-container {
					float: left;
					width: 100%;
					padding: 10px 10px 0px 10px;

					.progress-bar {
						position: relative;
						height: 8px;
						border-radius: 4px;
						background-color: #e3e3e3;

						.bar {
							height: 8px;
							border-radius: 4px;
							background-color: rgba($alert-success-colour, 0.7);
							width: 0%;
							@include prefix(transition, all 0.2s ease-in-out);
						}

						.bar-pulse {
							position: absolute;
							top: 0;
							left: 0;
							@include prefix(animation-name, progress-bar-pulse);
							@include prefix(animation-duration, 2s);
							@include prefix(animation-iteration-count, infinite);
							height: 8px;
							border-radius: 4px;
							background-color: $alert-success-colour;
							@include prefix(transition, all 0.2s ease-in-out);
						}
					}
				}
			}
		}
	}
</style>
